import { FC, useEffect, useState } from "react"
import { Button } from "primereact/button"
import { ToggleButton } from "primereact/togglebutton"
import { SelectButton } from "primereact/selectbutton"
import {
  SettingService,
  updateSettingsResponse,
  getSettingsResponse,
  GetAccessTokensResponse
} from "../../service/SettingService"
import { VeeqoAuthService } from "../../service/VeeqoAuthService"
import { OrdersSyncService } from "../../service/OrdersSyncService"
import moment from "moment"
import { SelectItemOptionsType } from "primereact/selectitem"
import { Divider } from "primereact/divider"
import { TiktokAuthService } from "../../service/TiktokAuthService"

const serviceId = {
  us: process.env.REACT_APP_TIKTOK_SERVICE_ID_US,
  uk: process.env.REACT_APP_TIKTOK_SERVICE_ID_UK
}

const tiktokAuthorizeUrl = {
  us: process.env.REACT_APP_TIKTOK_AUTHORIZATION_URL_US,
  uk: process.env.REACT_APP_TIKTOK_AUTHORIZATION_URL_UK
}

const syncDateOptions: SelectItemOptionsType = [
  {
    label: "Last 7 days",
    value: moment().subtract(7, "days").format("YYYY-MM-DD")
  },
  {
    label: "Last 30 days",
    value: moment().subtract(30, "days").format("YYYY-MM-DD")
  },
  {
    label: "All",
    value: moment().subtract(1, "year").format("YYYY-MM-DD")
  }
]

const regionOptions = [
  {
    label: "US",
    value: "us"
  },
  {
    label: "UK",
    value: "uk"
  }
]

export const SettingsPage: FC = () => {
  const [syncOrders, setSyncOrders] = useState<boolean>()
  const [pushInventory, setPushInventory] = useState<boolean>()
  const [pullOrders, setPullOrders] = useState<boolean>()
  const [linkProducts, setLinkProducts] = useState<boolean>()
  // const [dataSyncWithTikTok, setDataSyncWithTikTok] = useState<boolean>()
  // const [dataSyncWithVeeqo, setDataSyncWithVeeqo] = useState<boolean>()
  const [tiktokShop, setTikTokShop] = useState<string | undefined>(undefined)
  const [veeqoCompany, setVeeqoCompany] = useState<string | undefined>(undefined)
  const [loadingTikTok, setLoadingTikTok] = useState<boolean>()
  const [loadingVeeqo, setLoadingVeeqo] = useState<boolean>()
  const [loadingSyncOrders, setLoadingSyncOrders] = useState(false)
  const [loadingSettings, setLoadingSettings] = useState(false)
  const [userToken, setUserToken] = useState("")
  const [syncDate, setSyncDate] = useState<string>(syncDateOptions[0].value)
  const [hasSettingsChanged, setHasSettingsChanged] = useState(false)
  const [region, setRegion] = useState<"us" | "uk">("us")

  useEffect(() => {
    const userEmailAndCognitoEncoded = localStorage.getItem("accessToken") || ""
    setUserToken(userEmailAndCognitoEncoded)
  }, [])

  useEffect(() => {
    // setDataSyncWithTikTok(false)
    // setDataSyncWithVeeqo(false)
    setLoadingTikTok(true)
    setLoadingVeeqo(true)

    const initialize = async () => {
      setLoadingSettings(true)
      let initOrdersSync = false
      await SettingService.getSettings().then((res: getSettingsResponse) => {
        if (res) {
          if (res.sync_orders_tiktok_to_veeqo) {
            initOrdersSync = true
          }
          setPushInventory(res.push_inventory_changes_veeqo_to_tiktok)
          setSyncOrders(res.sync_orders_tiktok_to_veeqo)
          setLinkProducts(res.link_products_veeqo_tiktok_by_sku_code)
          setPullOrders(res.pull_orders_tiktok_on_hold_status)
        }
      })
      setLoadingSettings(false)

      await SettingService.getAccessTokens().then(async (res: GetAccessTokensResponse) => {
        setTikTokShop(res.tiktok_shop_name)
        setVeeqoCompany(res.veeqo_company_name)
        setLoadingTikTok(false)
        setLoadingVeeqo(false)
        if (res.tiktok_shop_name && res.veeqo_company_name && initOrdersSync) {
          setLoadingSyncOrders(true)
          await OrdersSyncService.initTiktokToVeeqoOrdersSync()
          setLoadingSyncOrders(false)
        }
      })
    }

    initialize()
  }, [])

  const submit = async () => {
    const reqBody = {
      sync_orders_tiktok_to_veeqo: syncOrders,
      push_inventory_changes_veeqo_to_tiktok: pushInventory,
      pull_orders_tiktok_on_hold_status: pullOrders,
      link_products_veeqo_tiktok_by_sku_code: linkProducts
    }
    await SettingService.updateSettings(reqBody).then((res: updateSettingsResponse) => res)
    setHasSettingsChanged(false)
  }

  const handleConnectTiktok = async () => {
    const fullTiktokAuthorizeUrl = `${tiktokAuthorizeUrl[region]}?service_id=${serviceId[region]}`

    const stateData = {
      userToken,
      region,
      locationUrl: window.location.href
    }

    const encodedStateData = encodeURIComponent(JSON.stringify(stateData))

    const fullUrlWithState = `${fullTiktokAuthorizeUrl}&state=${encodedStateData}`

    window.location.href = fullUrlWithState
  }

  const authorizeVeeqo = () => {
    const locationUrl = window.location.href.replace(/\//g, "_")

    VeeqoAuthService.authorize(userToken + "." + locationUrl)
  }

  const handleSyncOrders = async () => {
    setLoadingSyncOrders(true)
    await OrdersSyncService.initTiktokToVeeqoOrdersSync({ startDate: syncDate })
    setLoadingSyncOrders(false)
  }

  const handleSettings = <T,>(value: T, callback: (value: T) => void) => {
    if (!hasSettingsChanged) {
      setHasSettingsChanged(true)
    }
    callback(value)
  }

  const handleTikTokLogout = async () => {
    try {
      setLoadingTikTok(true)
      const response = await TiktokAuthService.logout()

      if (response.status === "error") {
        throw new Error("Error logging out from TikTok")
      }

      setTikTokShop(undefined)
      setLoadingTikTok(false)
    } catch (error) {
      console.error(error)
    }
  }

  const handleVeeqoLogout = async () => {
    try {
      setLoadingVeeqo(true)
      const response = await VeeqoAuthService.logout()

      if (response.status === "error") {
        throw new Error("Error logging out from Veeqo")
      }

      setVeeqoCompany(undefined)
      setLoadingVeeqo(false)
    } catch (error) {
      console.error(error)
    }
  }

  const isLoading = loadingSettings || loadingTikTok || loadingVeeqo

  return (
    <div className="p-5">
      <div className="flex justify-content-between mb-4">
        <div className="flex gap-2">
          <div className="flex gap-1">
            {!tiktokShop && (
              <SelectButton
                className="p-selectbutton p-button-group p-component h-full"
                value={region}
                onChange={(e) => setRegion(e.value)}
                options={regionOptions}
                tooltip="Select TikTok region"
                tooltipOptions={{
                  position: "top"
                }}
                disabled={loadingTikTok}
              />
            )}
            <Button
              label={
                tiktokShop
                  ? `TikTok Shop: ${tiktokShop}`
                  : loadingTikTok
                  ? "Syncing TikTok Auth"
                  : "Authenticate TikTok"
              }
              loading={loadingTikTok}
              icon={`pi ${tiktokShop ? "pi-check" : "pi-sign-in"}`}
              disabled={!!tiktokShop}
              className="p-button p-button-primary"
              onClick={handleConnectTiktok}
            />
            {tiktokShop && (
              <Button
                loading={loadingTikTok}
                disabled={loadingTikTok}
                icon="pi pi-sign-out"
                className="p-button p-button-danger"
                onClick={handleTikTokLogout}
              />
            )}
          </div>
          <Divider layout="vertical" className="my-0 py-0" />
          <div className="flex gap-1">
            <Button
              label={
                veeqoCompany
                  ? `Veeqo Company: ${veeqoCompany}`
                  : loadingVeeqo
                  ? "Syncing Veeqo Auth"
                  : "Authenticate Veeqo"
              }
              loading={loadingVeeqo}
              icon={`pi ${veeqoCompany ? "pi-check" : "pi-sign-in"}`}
              disabled={!!veeqoCompany}
              className="p-button p-button-primary"
              onClick={authorizeVeeqo}
            />
            {veeqoCompany && (
              <Button
                loading={loadingVeeqo}
                disabled={loadingVeeqo}
                icon="pi pi-sign-out"
                className="p-button p-button-danger"
                onClick={handleVeeqoLogout}
              />
            )}
          </div>
        </div>
      </div>
      <div className="card flex flex-column gap-1 justify-content-end p-4">
        <div className="flex gap-4 flex-1 justify-content-between align-items-center border-bottom-1 border-gray-200">
          <h3 className="w-full">Sync Orders from TikTok to Veeqo</h3>
          <ToggleButton
            checked={syncOrders}
            onChange={(e) => handleSettings(e.value, setSyncOrders)}
            disabled={loadingSettings}
            className={`w-6rem ${syncOrders ? "bg-primary" : ""}`}
          />
        </div>
        <div className="flex gap-4 flex-1 justify-content-between align-items-center border-bottom-1 border-gray-200">
          <h3 className="w-full">Push Inventory changes from Veeqo to TikTok</h3>
          <ToggleButton
            checked={pushInventory}
            onChange={(e) => handleSettings(e.value, setPushInventory)}
            disabled={loadingSettings}
            className={`w-6rem ${pushInventory ? "bg-primary" : ""}`}
          />
        </div>
        <div className="flex gap-4 flex-1 justify-content-between align-items-center">
          <h3 className="w-full">Link Products between Veeqo and TikTok by SKU code</h3>
          <ToggleButton
            checked={linkProducts}
            onChange={(e) => handleSettings(e.value, setLinkProducts)}
            disabled={loadingSettings}
            className={`w-6rem ${linkProducts ? "bg-primary" : ""}`}
          />
        </div>
        {hasSettingsChanged && (
          <Button
            label="Save Settings"
            className={`p-button ${hasSettingsChanged ? "p-button-success" : "p-button-secondary"}`}
            onClick={submit}
            disabled={!hasSettingsChanged}
          />
        )}
      </div>
      {!isLoading && (
        <div className="card p-4 w-70rem flex justify-content-around">
          {syncOrders ? (
            tiktokShop && veeqoCompany ? (
              <div className="flex flex-column justify-content-center items-center gap-4">
                <div className="text-center w-full">
                  <Button
                    label={loadingSyncOrders ? "Syncing orders" : "Sync Orders Now"}
                    loading={loadingSyncOrders}
                    icon="pi pi-refresh"
                    className="p-button p-button-success"
                    onClick={handleSyncOrders}
                    disabled={loadingSyncOrders}
                    size="large"
                  />
                  <div className="py-2 text-sm">from: </div>
                  <SelectButton
                    value={syncDate}
                    onChange={(e) => setSyncDate(e.value)}
                    options={syncDateOptions}
                    disabled={loadingSyncOrders}
                  />
                </div>
                <div className="text-center text-sm">The sync process will run in the background.</div>
              </div>
            ) : (
              <div>Get authorized on TikTok and Veeqo to Sync Orders.</div>
            )
          ) : (
            <div>
              Enable <strong>Sync Orders</strong> setting to fetch orders from TikTok to Veeqo.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
